import { RestHandler, MockedRequest, DefaultRequestBody, rest } from 'msw';
import { CreateGroupRequest } from '../../queries/use-create-group';
import { DeleteGroupRequest } from '../../queries/use-delete-groups';
import { PostResponseBody } from '../../types/api-response';
import { Group } from '../../types/group';
import { parseDate } from '../../utilities/formatting';
import { DB } from '../db';
import { paginate } from '../utils';
const routes: RestHandler<MockedRequest<DefaultRequestBody>>[] = [];

routes.push(
    /**
     * GET /groups
     * Gets all groups for a firm
     */
    rest.get('/groups', (req, res, ctx) => {
        // const rand = Math.floor(Math.random() * 10 + 1);
        const page = parseInt(req.url.searchParams.get('page') || '1');
        const limit = parseInt(req.url.searchParams.get('limit') || '10');
        const search = req.url.searchParams.get('search');
        const sort = req.url.searchParams.get('sort');
        const filter = req.url.searchParams.get('filter');

        let groups = DB.GetGroups();
        let totalItems = groups.length;

        if (search) {
            groups = groups.filter((g) => g.name.toLowerCase().includes(search.toLowerCase()));
            totalItems = groups.length;
        } else if (filter) {
            const filterObj = JSON.parse(decodeURIComponent(filter));

            if (filterObj.entity) {
                const value = filterObj.entity;
                groups = groups.filter((g) => g.entities.includes(value[0]));
            }
            if (filterObj.entities) {
                const { base, condition, to } = filterObj.entities;
                if (condition === '>') {
                    groups = groups.filter((g) => g.entities.length > base);
                } else if (condition === '=') {
                    groups = groups.filter((g) => g.entities.length === base);
                } else if (condition === '<') {
                    groups = groups.filter((g) => g.entities.length < base);
                } else {
                    groups = groups.filter((g) => g.entities.length >= base && g.entities.length <= to);
                }
            }
            if (filterObj.updatedAt) {
                const { base, condition, to } = filterObj.updatedAt;
                if (condition === '>') {
                    groups = groups.filter((g) => new Date(parseDate(g.updatedAt)) > new Date(base));
                } else if (condition === '=') {
                    groups = groups.filter((g) => parseDate(g.updatedAt) === base);
                } else if (condition === '<') {
                    groups = groups.filter((g) => new Date(parseDate(g.updatedAt)) < new Date(base));
                } else {
                    groups = groups.filter(
                        (g) => new Date(parseDate(g.updatedAt)) >= new Date(base) && new Date(parseDate(g.updatedAt)) <= new Date(to)
                    );
                }
            }
            if (filterObj.activeWorkpapers) {
                const { base, condition, to } = filterObj.activeWorkpapers;
                if (condition === '>') {
                    groups = groups.filter((g) => g.activeWorkpapers.length > base);
                } else if (condition === '=') {
                    groups = groups.filter((g) => g.activeWorkpapers.length === base);
                } else if (condition === '<') {
                    groups = groups.filter((g) => g.activeWorkpapers.length < base);
                } else {
                    groups = groups.filter((g) => g.activeWorkpapers.length >= base && g.activeWorkpapers.length <= to);
                }
            }
            totalItems = groups.length;
        }

        if (sort) {
            const { order, option } = JSON.parse(decodeURIComponent(sort));

            if (option === 'Name') {
                groups = groups.sort((a, b) => (a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1));
            } else if (option === 'Entities') {
                groups = groups.sort((a, b) => (a.entities.length > b.entities.length ? 1 : -1));
            } else if (option === 'Active Workpapers') {
                groups = groups.sort((a, b) => (a.activeWorkpapers.length > b.activeWorkpapers.length ? 1 : -1));
            } else if (option === 'Last Updated') {
                groups = groups.sort((a, b) => (a.updatedAt > b.updatedAt ? 1 : -1));
            }
            if (order === 'desc') {
                groups = groups.reverse();
            }
        }

        return res(ctx.delay(), ctx.status(200), ctx.json({ groups: paginate<Group>(groups, page, limit), totalItems }));
    }),

    /**
     * GET /groups/:id
     * Gets a group for a firm
     */
    rest.get('/groups/:id', (req, res, ctx) => {
        const id = req.params.id as string;
        const group = DB.GetGroup(id);

        return res(ctx.delay(), ctx.status(200), ctx.json({ group }));
    }),

    /**
     * POST /groups
     * Creates a new group
     */
    rest.post<CreateGroupRequest>('/groups', (req, res, ctx) => {
        const { groups: groupsReq } = req.body;

        const response: PostResponseBody<Group> = {
            hasErrors: false,
            results: [],
        };

        groupsReq.forEach(({ name, description, entities }) => {
            try {
                const group = DB.CreateGroup(name, entities, description);
                DB.CreateFolder(group.id, group.name);
                response.results.push({ statusCode: 201, data: group });
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
            } catch (error: any) {
                const fields: { [key: string]: string } = {};
                if (error.message === 'A group must have a name.') {
                    fields.name = 'A group must have a name.';
                }
                if (error.message === 'A group with this name already exists.') {
                    fields.name = 'A group with this name already exists.';
                }
                response.hasErrors = true;
                response.results.push({
                    statusCode: 400,
                    error: { original: { name, description, entities }, fields },
                });
            }
        });

        return res(ctx.delay(), ctx.status(response.hasErrors ? 400 : 200), ctx.json(response));
    }),

    rest.delete<DeleteGroupRequest>('/groups', (req, res, ctx) => {
        const ids = req.body.ids;
        ids.forEach((id) => {
            DB.DeleteGroup(id);
        });
        return res(ctx.delay(), ctx.status(204));
    })
);

export { routes };
